import { useCallback, useEffect, useRef, useState } from "react";
import { isTypingTarget } from "@/lib/keyboard";

interface UseTreeKeyboardOptions {
  expandNode?: (nodeId: string) => void;
  collapseNode?: (nodeId: string) => void;
}

/**
 * Returns all visible treeitem elements within a tree container.
 * Filters out items inside collapsed ancestors (unmounted by Base UI Collapsible).
 */
function getVisibleTreeItems(tree: HTMLElement): HTMLElement[] {
  const all = Array.from(tree.querySelectorAll<HTMLElement>('[role="treeitem"]'));
  return all.filter((el) => {
    let node = el.parentElement;
    while (node && node !== tree) {
      if (node instanceof HTMLElement && node.hasAttribute("hidden")) return false;
      node = node.parentElement;
    }
    return true;
  });
}

function moveFocus(items: HTMLElement[], fromIndex: number, toIndex: number) {
  const from = items[fromIndex];
  const to = items[toIndex];
  if (!from || !to) return;
  from.setAttribute("tabindex", "-1");
  to.setAttribute("tabindex", "0");
  to.focus();
}

function expandNodeViaDOM(treeitem: HTMLElement) {
  if (treeitem.getAttribute("aria-expanded") === "false") {
    // Find toggle: explicit [data-tree-toggle] or direct child CollapsibleTrigger
    const toggle = treeitem.querySelector<HTMLElement>(
      "[data-tree-toggle], :scope > [data-slot='collapsible-trigger']"
    );
    if (toggle) toggle.click();
  }
}

function collapseNodeViaDOM(treeitem: HTMLElement) {
  if (treeitem.getAttribute("aria-expanded") === "true") {
    const toggle = treeitem.querySelector<HTMLElement>(
      "[data-tree-toggle], :scope > [data-slot='collapsible-trigger']"
    );
    if (toggle) toggle.click();
  }
}

/**
 * Adds WAI-ARIA TreeView keyboard navigation to a tree container.
 *
 * Implements:
 * - Arrow Up/Down: Navigate between visible treeitems
 * - Arrow Right: Expand closed node, or move to first child of open node
 * - Arrow Left: Collapse open node, or move to parent
 * - Home/End: Jump to first/last visible treeitem
 * - Enter: Activate node (navigate for links, toggle for folders)
 * - Space: Toggle expand/collapse
 *
 * Uses roving tabindex pattern: one item has tabIndex=0, rest have tabIndex=-1.
 *
 * @param treeRef - Callback ref setter for the element with role="tree"
 * @param options - Optional expandNode/collapseNode callbacks for state-managed trees.
 *                  Falls back to clicking the [data-tree-toggle] element if not provided.
 */
export function useTreeKeyboard(options?: UseTreeKeyboardOptions) {
  const [tree, setTree] = useState<HTMLElement | null>(null);
  const optionsRef = useRef(options);
  optionsRef.current = options;

  // Callback ref to attach to the tree container element
  const treeCallbackRef = useCallback((node: HTMLElement | null) => {
    setTree(node);
  }, []);

  useEffect(() => {
    if (!tree) return;

    // Initialize roving tabindex: all items get -1, first (or selected) gets 0
    const initItems = getVisibleTreeItems(tree);
    const selected = initItems.find((el) => el.getAttribute("aria-selected") === "true");
    const initialFocus = selected ?? initItems[0];
    if (initialFocus) {
      initialFocus.setAttribute("tabindex", "0");
    }

    function doExpand(nodeId: string, treeitem: HTMLElement) {
      if (optionsRef.current?.expandNode) {
        optionsRef.current.expandNode(nodeId);
      } else {
        expandNodeViaDOM(treeitem);
      }
    }

    function doCollapse(nodeId: string, treeitem: HTMLElement) {
      if (optionsRef.current?.collapseNode) {
        optionsRef.current.collapseNode(nodeId);
      } else {
        collapseNodeViaDOM(treeitem);
      }
    }

    function handleKeyDown(e: KeyboardEvent) {
      if (isTypingTarget(e.target)) {
        return;
      }

      const items = getVisibleTreeItems(tree!);
      if (items.length === 0) return;

      // Find currently focused treeitem
      const focused = document.activeElement as HTMLElement;
      let currentIndex = items.indexOf(focused);

      // If focus is inside a treeitem (e.g., on an inner button), find the closest treeitem
      if (currentIndex === -1 && focused) {
        const closest = focused.closest('[role="treeitem"]') as HTMLElement | null;
        if (closest) currentIndex = items.indexOf(closest);
      }
      if (currentIndex === -1) return;

      const current = items[currentIndex] as HTMLElement | undefined;
      if (!current) return;

      switch (e.key) {
        case "ArrowDown": {
          e.preventDefault();
          if (currentIndex < items.length - 1) {
            moveFocus(items, currentIndex, currentIndex + 1);
          }
          break;
        }

        case "ArrowUp": {
          e.preventDefault();
          if (currentIndex > 0) {
            moveFocus(items, currentIndex, currentIndex - 1);
          }
          break;
        }

        case "ArrowRight": {
          e.preventDefault();
          const expanded = current.getAttribute("aria-expanded");
          if (expanded === "false") {
            // Expand closed node (focus stays)
            const nodeId = current.dataset.treeNodeId;
            if (nodeId) doExpand(nodeId, current);
          } else if (expanded === "true") {
            // Move focus to first child
            if (currentIndex < items.length - 1) {
              moveFocus(items, currentIndex, currentIndex + 1);
            }
          }
          break;
        }

        case "ArrowLeft": {
          e.preventDefault();
          const expanded = current.getAttribute("aria-expanded");
          if (expanded === "true") {
            // Collapse open node (focus stays)
            const nodeId = current.dataset.treeNodeId;
            if (nodeId) doCollapse(nodeId, current);
          } else {
            // Move focus to parent treeitem
            const group = current.closest('[role="group"]');
            if (group) {
              const parentItem = group.closest('[role="treeitem"]') as HTMLElement | null;
              if (parentItem) {
                const parentIndex = items.indexOf(parentItem);
                if (parentIndex >= 0) {
                  moveFocus(items, currentIndex, parentIndex);
                }
              }
            }
          }
          break;
        }

        case "Home": {
          e.preventDefault();
          if (currentIndex !== 0) {
            moveFocus(items, currentIndex, 0);
          }
          break;
        }

        case "End": {
          e.preventDefault();
          if (currentIndex !== items.length - 1) {
            moveFocus(items, currentIndex, items.length - 1);
          }
          break;
        }

        case "Enter": {
          e.preventDefault();
          if (current.tagName === "BUTTON") {
            // Leaf node: the treeitem itself is the button
            current.click();
          } else {
            const activationTarget = current.matches("[data-tree-activate]")
              ? current
              : current.querySelector<HTMLElement>(":scope [data-tree-activate]");
            if (activationTarget) {
              activationTarget.click();
            } else {
              // Pure folder: toggle expand/collapse
              const nodeId = current.dataset.treeNodeId;
              if (nodeId) {
                if (current.getAttribute("aria-expanded") === "true") {
                  doCollapse(nodeId, current);
                } else {
                  doExpand(nodeId, current);
                }
              }
            }
          }
          break;
        }

        case " ": {
          e.preventDefault();
          const nodeId = current.dataset.treeNodeId;
          const expandedState = current.getAttribute("aria-expanded");
          if (nodeId && expandedState !== null) {
            // Parent node: toggle expand/collapse
            if (expandedState === "true") {
              doCollapse(nodeId, current);
            } else {
              doExpand(nodeId, current);
            }
          } else if (current.tagName === "BUTTON") {
            // Leaf node: activate
            current.click();
          }
          break;
        }

        default:
          return; // Don't prevent default for unhandled keys
      }
    }

    tree.addEventListener("keydown", handleKeyDown);
    return () => tree.removeEventListener("keydown", handleKeyDown);
  }, [tree]);

  return treeCallbackRef;
}
